import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { ExternalLink, MonitorSmartphone } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useMe } from "@/auth/use-permission";
import { NativeSelect } from "@/components/field";
import { openAsMember, useDemoOrg, useImpersonateMember } from "./queries";

/**
 * Dashboard shortcut for superadmins: open the DEMO01 cloud workstation (or its customer
 * portal) signed in as one of the demo organisation's members, without knowing a password.
 * Every use is audited as an impersonation.
 */
export function DemoWorkstationCard() {
  const isSuperadmin = useMe().data?.role === "superadmin";
  const demo = useDemoOrg();
  const [memberId, setMemberId] = useState("");
  const orgId = demo.data?.org.id ?? "";
  const impersonate = useImpersonateMember(orgId);
  if (!isSuperadmin || demo.isPending || !demo.data) return null;
  const { org, members } = demo.data;
  const selected = memberId || members[0]?.id || "";

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Demo workstation</CardTitle>
        <Link to={`/orgs/${org.id}`} className="text-primary text-sm hover:underline">
          {org.code} · {org.name}
        </Link>
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            The demo organisation has no members yet — invite one from the organisation page.
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1 text-muted-foreground text-xs">
              <label htmlFor="demo-member">Sign in as</label>
              <NativeSelect
                id="demo-member"
                value={selected}
                onChange={(e) => setMemberId(e.target.value)}
              >
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.email} · {m.role}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <Button
              onClick={() => openAsMember(impersonate, selected, "workstation")}
              disabled={impersonate.isPending}
              title="Opens app.<domain> signed in as this member (audited)"
            >
              <MonitorSmartphone /> Open demo workstation
            </Button>
            <Button
              variant="outline"
              onClick={() => openAsMember(impersonate, selected, "portal")}
              disabled={impersonate.isPending}
            >
              <ExternalLink /> Customer portal
            </Button>
            <span className="text-muted-foreground text-xs">
              One-time link, valid for an hour; the session shows a “Staff view” badge.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
